import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { InfoCircle, Download, FiletypeHtml } from 'react-bootstrap-icons'

import './Card.scss'
import { getFileIcon } from './Utils/getFileIcon'
import { APIClient, checkPollinationPanel, 
  getHost, useArtifacts, useHbjsontoVTK, useVsftoVTK, useVSFToHTML } from '../../hooks'
import SendModel from '../../SendModel/SendModel'
import { Action } from '../../SendModel/SendModel.types'
import SendResults from '../../SendResults/SendResults'
import LoadingIcon from '../../atoms/LoadingIcon/LoadingIcon'
import { usePollinationPanel } from '../../hooks/usePollinationPanel'
import { Run } from '@pollination-solutions/pollination-sdk'
import { FileCardConfig, LocalRun } from '../RunDetails.types'
import JSZip from 'jszip'
import { CardContext } from '../RunDetails'

export interface FileCardProps {
  projectOwner?: string
  projectName?: string
  client?: APIClient
  run?: Run | LocalRun
  label: string
  path: string
  description: string
  file?: Blob | File | string | undefined
  previewFile?: Blob | File | string | undefined
  previewName?: string
  onClicked?: (card: FileCardProps) => any
  isCloud?: boolean
  name?: string
}

interface ICADdata {
  data: object
  id: string
  type: CADtypeEnum
}

enum CADtypeEnum {
  HBJSON,
  VSF
}

/**
 * Download a file
 * @param blob Blob to convert
 * @param fileName File name to download
 */
const download = (blob: Blob, fileName: string) => {
  const file = new File([blob], fileName)
  const a = document.createElement('a')

  document.body.appendChild(a)
  a.style.display = 'none'
  a.id = 'download'
  const url = window.URL.createObjectURL(file)
  a.href = url
  a.download = fileName
  a.click()
}

export const FileCard: React.FC<FileCardProps> = ({
  projectOwner, 
  projectName, 
  client,
  run,
  label,
  path,
  file,
  description,
  onClicked,
  isCloud=true
}) => {
  const host = getHost()
  const { fromFileToBase64 } = usePollinationPanel()
  const { loading, html, fetchHTML } = useVSFToHTML()

  const parentConfig: FileCardConfig = useContext(CardContext)

  // VTKJS Handlers
  const { 
    loading: loadingHBJSON, 
    vtk: vtkHBJSON, 
    fetchVTK: fetchVTKfromHBJSON 
  } = useHbjsontoVTK()

  const { 
    loading: loadingVSF, 
    vtk: vtkHBVSF, 
    fetchVTK: fetchVTKfromVSF 
  } = useVsftoVTK()


  const { downloadArtifact } = useArtifacts(projectOwner, projectName, client)

  const [open, setOpen] = useState<boolean>(false)
  
  // Hover effects
  const [infoOver, setInfoOver] = useState(false)
  const [downloadOver, setDownloadOver] = useState(false)
  const [htmlOver, setHtmlOver] = useState(false)
  
  const [isLoading, setIsLoading] = useState(true)
  const [isInactive, setIsInactive] = useState(false)
  
  // Data to use for CAD injections
  const [CADdata, setCADdata] = useState<ICADdata>()
  
  // File to send to parent
  const [currFile, setCurrFile] = useState<Blob | File | string | undefined>()
  const [currPreview, setCurrPreview] = useState<Blob | File | string | undefined>()
  
  // HTML integration
  const [hasHtml, setHasHtml] = useState(false)
  
  // ZIP integration
  const [innerName, setInnerName] = useState<string>()

  /**
   * Get file name to use
   */
  const filename = useMemo(() => {
    if (!path) return

    return innerName ?? path.split('/').reverse()[0]
  }, [path, innerName])

  useEffect(() => {
    if (!vtkHBJSON) return

    if (filename.endsWith('hbjson')) {
      setCurrPreview(vtkHBJSON)
    }
  }, [vtkHBJSON])

  useEffect(() => {
    if (!vtkHBVSF) return

    if (filename.endsWith('vsf')) {
      setCurrPreview(vtkHBVSF)
    }
  }, [vtkHBVSF])

  /**
   * Preview name
   */
  const previewName = useMemo(() => {
    if (!currPreview || !filename) return

    let prevName
    if (filename.endsWith('zip')) {
      prevName = filename.replace('.zip', 'vtkjs')
    } else if (filename.endsWith('hbjson')) {
      prevName = filename.replace('hbjson', 'vtkjs')
    } else if (filename.endsWith('vsf')) {
      prevName = filename.replace('vsf', 'vtkjs')
    }
    return prevName
  }, [currPreview, filename])

  /**
   * Get file from a ZIP file.
   * 1 zip = 1 file for now
   */
  const getFileFromZip = useCallback((file: Blob) => {
    if (!file) return

    const archive = new JSZip()
    archive.loadAsync(file)
      .then(async (zip: JSZip) => {
        const entries = Object.keys(zip.files).map((name: string) => zip.files[name])
        const listOfPromises = entries.map((entry) => {
          return entry
            .async('blob')
            .then((blob): [string, Blob] => [entry.name, blob])
        })
        const promiseOfList = Promise.all(listOfPromises)

        promiseOfList.then((list) => {
          list.forEach(([name, f]) => {
            // 1 zip = 1 file
            // I assume text/plain
            setInnerName(name)

            const blob = f.slice(0, f.size, 'text/plain')
            setCurrFile(blob)
            // Note: inputs are always available
            if (name.endsWith('vsf')) {
              setHasHtml(true)
              fetchHTML(blob)
              setCADintegration(blob, name)
              fetchVTKfromVSF(blob)
            }
            })
          })
      })
  }, [file])

  /**
   * Get CAD data to use with the panel
   */
  const setCADintegration = useCallback((file: Blob, 
    name: string = undefined) => {
    if (!file || !filename) return 

    if (host === 'web') return

    if (filename.endsWith('hbjson') 
      && host === 'rhino') {
      const reader = new FileReader()
      reader.readAsText(file as Blob)
      reader.onload = () => {
        const res = reader?.result as string
        setCADdata( {
          data: JSON.parse(res),
          id: path,
          type: CADtypeEnum.HBJSON} )
      }
    } else if ((filename.endsWith('vsf') || name?.endsWith('vsf'))
      && (host === 'rhino' || host === 'revit')) {
      const reader = new FileReader()
      reader.readAsText(file as Blob)
      reader.onload = () => {
        const res = reader?.result as string
        setCADdata( {
          data: JSON.parse(res),
          id: path,
          type: CADtypeEnum.VSF} )
      }     
    }
  }, [file, filename])

  /**
   * Fetch file from cloud
   */
  const fetchAndSet = (url) => {
    fetch(url)
      .then((res: Response) => {
        if (res.status === 404) {
          // Deleted from cloud
          setIsInactive(true)
          setCurrFile(undefined)
          setCurrPreview(undefined)
          setCADintegration(undefined)
          setIsLoading(false)
          setHasHtml(false)
          setInnerName(undefined)
          return
        }
        res.blob()
        .then(blob => {
          // File is a ZIP
          if (blob.type === 'application/zip') {
            getFileFromZip(blob)
          } else {
            setCurrFile(blob)
            setInnerName(undefined)
            if (path?.endsWith('vsf')) {
              setHasHtml(true)
              fetchHTML(blob) // HTML button
              setCADintegration(blob)
              fetchVTKfromVSF(blob)
            } else if (path?.endsWith('hbjson')) {
              setHasHtml(false)
              setCADintegration(blob)
              fetchVTKfromHBJSON(blob)
            } else {
              setHasHtml(false)
            }
          }
          setIsLoading(false)
          setIsInactive(false)
        })
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
        setIsInactive(true)
      })
  }

  /**
   * Read local file as base64 - Panel only
   */
  const base64AndFetch = (path: string) => {
    if (!path || !filename) return

    const data = fromFileToBase64(path)
    const base64Data = data?.data

    if (!base64Data) {
      // Not found...
      setIsInactive(true)
      setCurrFile(undefined)
      setCADintegration(undefined)
      setIsLoading(false)
      return
    }

    // bas64 mapping
    let t
    if (filename.endsWith('zip')) {
      t = 'application/zip'
    } else if (filename.endsWith('hbjson')) {
      t = 'text/plain'
    } else if (filename.endsWith('vsf')) {
      t = 'text/plain'
    } else {
      t = 'text/plain'
    }

    fetchAndSet(`data:${t};base64,${base64Data}`)
  }

  const isPanel = useMemo(() => checkPollinationPanel(), [])

  useEffect(() => {
    if (isCloud) {
      // Generate Blob on click
      run && downloadArtifact(path, run.status.job_id)
        .then((url: string) => fetchAndSet(url))
        .catch(() => onClicked({ 
          label, 
          path, 
          file: undefined, 
          description, 
          name: filename 
        }))
    } else {
      // Some inputs has path == null
      if (!isPanel || !path) {
        setIsInactive(true)
        setCurrFile(undefined)
        setCADintegration(undefined)
        setIsLoading(false)
      } else {
        base64AndFetch(path)
      }
    }
  }, [run, label, path])

  return <div className={ isInactive 
      ? 'card-param card-folder-inactive' 
      : (loadingHBJSON || loadingVSF) 
      ?  'card-param'
      : 'card-param card-folder'}
    tabIndex={0}
    role={(loadingHBJSON || loadingVSF) ? 'none' : 'button'}
    key={label}
    onClick={(event) => {
      event.stopPropagation()
      if (isInactive || (loadingHBJSON || loadingVSF)) return

      onClicked({ label, 
        path, 
        file: currFile, 
        description, 
        name: filename,
        previewFile: currPreview ?? currFile,
        previewName: previewName ?? filename 
      })
      }}>
    <div className='title-param'>
      <div style={{ float: 'left', margin: '0 8px 0 0' }}>
        {(!isLoading) ? <div
          title={!isInactive 
            ? 'Open the viewer' 
            : 'File is missing. Check run workspace'}>
          <span style={{ margin: '0 8px 0 0' }}>
            {path && filename && getFileIcon(18, filename?.split('.')?.pop() || '')}
          </span>
          {label}
        </div>
        : <LoadingIcon size={14}/>}

      </div>
      {!isLoading && <div style={{ textAlign: 'right' }}>
        {(filename && hasHtml) && (loading ? <LoadingIcon size={14}/> : 
          (
            <button style={{ all: 'unset', margin: '0 0 0 8px' }}
            title='Download HTML'
            onMouseOver={() => setHtmlOver(true)}
            onMouseLeave={() => setHtmlOver(false)}
            onClick={(event) => {
              if (!html) return
              setHtmlOver(false)
              event.stopPropagation()
              download(html, `${label}.html`)
            }}>
            <FiletypeHtml size={14} style={htmlOver ? { color: '#40a9ff' } : {}}/>
            </button>
          )
        )}
        
        <button style={{ all: 'unset', margin: '0 0 0 8px' }}
          title='Download'
          onMouseOver={() => setInfoOver(true)}
          onMouseLeave={() => setInfoOver(false)}
          onClick={(event) => {
            if (!currFile || !path) return
            event.stopPropagation()
            download(currFile as Blob, filename)
          }}>
          <Download size={14} style={infoOver ? { color: '#40a9ff' } : {}}/>
        </button>
        <button style={{ all: 'unset', margin: '0 0 0 8px' }}
          title='Description'
          onMouseOver={() => setDownloadOver(true)}
          onMouseLeave={() => setDownloadOver(false)}
          onClick={(event) => {
            event.stopPropagation()
            setOpen(prev => !prev)
          }}>
          <InfoCircle size={14} style={downloadOver ? { color: '#40a9ff' } : {}}/>
        </button>
      </div>}
    </div>
    { CADdata && <hr /> }
    <div className='value-param'>
      { CADdata && CADdata.type === CADtypeEnum.HBJSON && <SendModel
        hbjson={CADdata.data} 
        defaultAction={Action.preview}
        optionsConfig={{
          add: true,
          delete: false,
          'subscribe-preview': false,
          clear: false,
          preview: true,
          replace: true,
        }}
        key={CADdata.id}
        defaultKey={CADdata.id} /> }
      { CADdata && CADdata.type === CADtypeEnum.VSF && <SendResults
        results={CADdata.data} 
        // @ts-ignore
        defaultAction={parentConfig?.defaultAction ?? ((host === 'revit') ? Action.add : Action.preview)}
        optionsConfig={parentConfig?.optionsConfig ?? {
          add: true,
          delete: (host === 'revit'),
          'subscribe-preview': false,
          clear: false,
          preview: (host !== 'revit')
        }}
        key={CADdata.id}
        defaultKey={CADdata.id} /> }
    </div>

    {open && <div className='card-description'>{description}</div>}
  </div>
}

export default FileCard

